import {useCallback, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation, Trans} from 'react-i18next';
import {useQueryClient} from '@tanstack/react-query';
import ChekinPro from 'ChekinPro';
import ChekinProSettings from 'ChekinProSettings';
import {SDK_MODE} from 'utils/embedded';
import {BookingSummary} from '@guestapp/sdk';
import {useModalControls, usePath, useTimeoutRef} from 'hooks';
import {useAuth} from 'hooks/auth/useAuth';
import {useSummary} from 'context/summary';
import {StatusModalStates} from 'components/StatusModal';
import {QUERY_CACHE_KEYS} from 'utils/constants';
import {PATHS} from 'Routes';
import {ModalGuestRegisteredNextStepContent} from './ModalGuestRegisteredNextStep';
import {GuestsProcessingModal} from './GuestsProcessingModal';
import {SignUpSuggestionModal} from './SignUpSuggestionModal';
import {StyledModalSheet} from './styled';

const TimeoutFinishProcess = 3000;

const getTitle = (state?: StatusModalStates, defaultTitle?: string) => {
  if (state === StatusModalStates.LOADING) {
    return 'sending_your_data';
  } else if (defaultTitle) {
    return defaultTitle;
  } else if (state === StatusModalStates.ERROR) {
    return 'error';
  } else {
    return 'guest_registered';
  }
};

type ModalTypes = {
  resetForm?: () => void;
  onClose: () => void;
  name?: string;
  state?: StatusModalStates;
  errorMessage?: string;
  modalTitle?: string;
  isEdition?: boolean;
};

function GuestRegisteringModal({
  name,
  state,
  onClose,
  errorMessage,
  modalTitle,
  isEdition,
}: ModalTypes) {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const title = getTitle(state, modalTitle);
  const timeoutRef = useTimeoutRef();
  const queryClient = useQueryClient();
  const {isLoggedIn} = useAuth();
  const {isInstantCheckInEnabled} = useSummary();

  const {
    isOpen: isOpenNextStepModal,
    openModal: openNextStepModal,
    closeModal: closeNextStepModal,
  } = useModalControls();
  const {
    isOpen: isOpenSignUpModal,
    openModal: openSignUpModal,
    closeModal: closeSignUpModal,
  } = useModalControls();

  const finishProcess = useCallback(() => {
    const summary = queryClient.getQueryData<BookingSummary>([QUERY_CACHE_KEYS.summary]);
    const isAllRegistered = summary?.guest_registration?.status === 'COMPLETE';
    if (isAllRegistered) {
      ChekinPro.events.onAllGuestsRegistered();
      navigate(getFullPath(PATHS.home));
      return;
    } else if (ChekinProSettings.mode === SDK_MODE.guestRegistrationOnly) {
      navigate(getFullPath(PATHS.home));
      return;
    }
    openNextStepModal();
  }, [queryClient, navigate, getFullPath, openNextStepModal]);

  const shouldShowSignUpModal = !isLoggedIn && isInstantCheckInEnabled;
  const onFinishProcess = useCallback(() => {
    if (shouldShowSignUpModal) {
      openSignUpModal();
    } else {
      finishProcess();
    }
  }, [shouldShowSignUpModal, openSignUpModal, finishProcess]);

  useEffect(() => {
    if (state === StatusModalStates.SUCCESS) {
      timeoutRef.current = setTimeout(() => {
        onFinishProcess();
      }, TimeoutFinishProcess);
    }
  }, [state, timeoutRef, onFinishProcess]);

  const onSignUp = () => {
    closeSignUpModal();
    navigate(getFullPath(PATHS.auth.createAccount));
  };

  const onKeepEnteringDataManually = () => {
    closeSignUpModal();
    finishProcess();
  };

  return (
    <>
      <GuestsProcessingModal
        onClose={onClose}
        title={t(title)}
        state={state}
        errorMessage={errorMessage}
        successSubtitle={
          <Trans
            i18nKey={
              isEdition
                ? 'guest_updated_modal_description'
                : 'guest_registered_modal_description'
            }
            values={{name}}
          />
        }
      />
      <StyledModalSheet
        isOpenModalSheet={isOpenNextStepModal}
        handleCloseModal={closeNextStepModal}
        closeOnDocumentClick={false}
        withModalMobileCloseButton={false}
        closeButtonType="none"
      >
        <ModalGuestRegisteredNextStepContent closeModal={closeNextStepModal} />
      </StyledModalSheet>
      <StyledModalSheet
        isOpenModalSheet={isOpenSignUpModal}
        handleCloseModal={closeSignUpModal}
        closeOnDocumentClick={false}
        withModalMobileCloseButton={false}
        closeButtonType="none"
      >
        <SignUpSuggestionModal
          onKeepEnteringDataManually={onKeepEnteringDataManually}
          onSignUp={onSignUp}
        />
      </StyledModalSheet>
    </>
  );
}

export {GuestRegisteringModal};
